import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsX, BsPlusCircle } from 'react-icons/bs';
import dispatchService, { CreateTaskPayload, DispatchTask } from '../../services/dispatchService';

interface TaskPostingFormProps {
    onClose: () => void;
    onSuccess: () => void;
    initialData?: DispatchTask;
}

const TaskPostingForm: React.FC<TaskPostingFormProps> = ({ onClose, onSuccess, initialData }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [timeline, setTimeline] = useState('');
    const [budgetMin, setBudgetMin] = useState('');
    const [budgetMax, setBudgetMax] = useState('');
    const [category, setCategory] = useState<'professional' | 'semi-skilled' | 'informal' | 'emergency'>('professional');
    const [type, setType] = useState<'full-time' | 'part-time' | 'contract' | 'gig' | 'one-day' | 'hourly'>('contract');
    const [mode, setMode] = useState<'remote' | 'on-site' | 'hybrid' | 'field'>('remote');
    const [urgency, setUrgency] = useState<'immediate' | 'scheduled' | 'flexible'>('flexible');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [payStructure, setPayStructure] = useState<'fixed' | 'hourly' | 'daily' | 'task-based'>('fixed');
    const [currency, setCurrency] = useState('USD');

    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const [currentSkill, setCurrentSkill] = useState('');
    const [requiresCV, setRequiresCV] = useState(true);
    const [toolsRequired, setToolsRequired] = useState(false);
    const [physicalEffort, setPhysicalEffort] = useState<'none' | 'light' | 'moderate' | 'heavy'>('none');
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['English']);
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (initialData) {
            setTitle(initialData.title);
            setDescription(initialData.description);
            setTimeline(initialData.timeline || '');
            if (initialData.budgetRange) {
                setBudgetMin(initialData.budgetRange.min?.toString() || '');
                setBudgetMax(initialData.budgetRange.max?.toString() || '');
                // Cast to any because the interface might not be fully updated in frontend yet if I missed it, 
                // but DispatchTask definition I saw earlier didn't have currency. 
                // Wait, I didn't update DispatchTask interface in frontend yet! 
                // I should probably do that too, but local cast is fine for now if backend sends it.
                // Actually I should update the interface. For now let's assume it comes.
                if ((initialData.budgetRange as any).currency) setCurrency((initialData.budgetRange as any).currency);
            }
            if (initialData.category) setCategory(initialData.category);
            if (initialData.type) setType(initialData.type);
            if (initialData.mode) setMode(initialData.mode);
            if (initialData.urgency) setUrgency(initialData.urgency);
            if (initialData.location) {
                setCity(initialData.location.city || '');
                setCountry(initialData.location.country || '');
            }
            if (initialData.payStructure) setPayStructure(initialData.payStructure);

            if (initialData.requiredSkills) {
                // Handle both populated (objects) and unpopulated (strings) just in case
                setSelectedSkills(initialData.requiredSkills.map((s: any) => typeof s === 'string' ? s : s.name));
            }

            // New fields - using explicit checks as they might be undefined in older records
            if (initialData.toolsRequired !== undefined) setToolsRequired(initialData.toolsRequired);
            if (initialData.physicalEffort) setPhysicalEffort(initialData.physicalEffort as any);
            if (initialData.languageRequirements) setSelectedLanguages(initialData.languageRequirements);
            if (initialData.requiresCV !== undefined) setRequiresCV(initialData.requiresCV);
        }
    }, [initialData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Auto-add current skill if user forgot to press Enter
        let finalSkills = [...selectedSkills];
        if (currentSkill.trim()) {
            const skill = currentSkill.trim();
            if (!finalSkills.includes(skill)) {
                finalSkills.push(skill);
            }
        }

        if (finalSkills.length === 0) {
            setError('"requiredSkills" must contain at least 1 items');
            return;
        }

        setLoading(true);
        try {
            const payload: CreateTaskPayload = {
                title,
                description,
                requiredSkills: finalSkills,
                timeline: timeline || undefined,
                budgetRange: budgetMin || budgetMax ? {
                    min: budgetMin ? Number(budgetMin) : undefined,
                    max: budgetMax ? Number(budgetMax) : undefined,
                    currency
                } : undefined,
                category,
                type,
                mode,
                urgency,
                payStructure,
                location: mode !== 'remote' ? { city, country } : undefined,
                requiresCV,
                toolsRequired,
                physicalEffort,
                languageRequirements: selectedLanguages
            };

            if (initialData) {
                await dispatchService.updateTask(initialData._id, payload);
            } else {
                await dispatchService.createTask(payload);
            }
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to post task');
        } finally {
            setLoading(false);
        }
    };

    const addSkill = () => {
        // Split by comma to handle pasted lists
        const newSkills = currentSkill
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);

        if (newSkills.length === 0) return;

        // Filter out duplicates
        const uniqueNewSkills = newSkills.filter(s => !selectedSkills.includes(s));

        if (uniqueNewSkills.length > 0) {
            setSelectedSkills([...selectedSkills, ...uniqueNewSkills]);
            setCurrentSkill('');
        }
    };

    const addLanguage = () => {
        const lang = currentLanguage.trim();
        if (lang && !selectedLanguages.includes(lang)) {
            setSelectedLanguages([...selectedLanguages, lang]);
            setCurrentLanguage('');
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
                style={{ zIndex: 9999 }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-gradient-to-br from-[#2C1810] to-[#1a0f08] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative flex flex-col"
                    style={{
                        zIndex: 10000,
                        pointerEvents: 'auto',
                        border: '1px solid rgba(212, 175, 55, 0.3)'
                    }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex-shrink-0 flex items-center justify-between p-6 sm:p-8 border-b border-[#D4AF37]/10 bg-black/20">
                        <h2 className="text-2xl sm:text-3xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold leading-tight">
                            {initialData ? 'Edit Dispatch Task' : 'Post Dispatch Task'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-[#D4AF37] hover:text-white transition-colors"
                        >
                            <BsX size={32} />
                        </button>
                    </div>

                    {/* Scrollable Form Body */}
                    <div className="overflow-y-auto flex-1 custom-scrollbar">

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 p-5 sm:p-8 pt-2">
                            {/* Task Title */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <label className="block text-[#D4AF37] font-semibold mb-2">
                                    Task Title *
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g., Build React dashboard for analytics SaaS"
                                    className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                                    style={{ pointerEvents: 'auto' }}
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <label className="block text-[#D4AF37] font-semibold mb-2">
                                    Description *
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe the work in detail..."
                                    rows={4}
                                    className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                                    style={{ pointerEvents: 'auto' }}
                                    required
                                />      </div>

                            {/* Required Skills */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <label className="block text-[#D4AF37] font-semibold mb-2">
                                    Required Skills
                                </label>
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={currentSkill}
                                            onChange={(e) => setCurrentSkill(e.target.value)}
                                            placeholder="e.g., React, Node.js (Type & Press Enter)"
                                            className="flex-1 px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                                            style={{ pointerEvents: 'auto' }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ',') {
                                                    e.preventDefault();
                                                    addSkill();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={addSkill}
                                            className="px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-lg text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#2C1810] transition-all"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {selectedSkills.length > 0 ? (
                                        <div className="flex flex-wrap gap-2 p-2 bg-black/20 rounded-lg border border-[#D4AF37]/20 min-h-[50px]">
                                            {selectedSkills.map((skill, index) => (
                                                <div
                                                    key={index}
                                                    className="bg-[#D4AF37] text-[#2C1810] px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 animate-fadeIn"
                                                >
                                                    {skill}
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedSkills(selectedSkills.filter((_, i) => i !== index))}
                                                        className="hover:bg-black/20 rounded-full p-0.5 transition-colors"
                                                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                                    >
                                                        <BsX size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-sm italic">Type a skill above and press Enter (or click Add) to include it.</p>
                                    )}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <label className="block text-[#D4AF37] font-semibold mb-2">
                                    Timeline (optional)
                                </label>
                                <input
                                    type="text"
                                    value={timeline}
                                    onChange={(e) => setTimeline(e.target.value)}
                                    placeholder="e.g., 2 weeks, 1 month"
                                    className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                                    style={{ pointerEvents: 'auto' }}
                                />
                            </div>

                            {/* Category & Type */}
                            <div className="grid grid-cols-2 gap-4" style={{ pointerEvents: 'auto' }}>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={category}
                                        onChange={(e) => {
                                            const val = e.target.value as any;
                                            setCategory(val);
                                            // Auto-disable CV requirement for informal/labor to match "Instant" feel
                                            if (val === 'informal' || val === 'emergency') {
                                                setRequiresCV(false);
                                            } else {
                                                setRequiresCV(true);
                                            }
                                        }}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] transition-colors appearance-none"
                                    >
                                        <option value="professional">Professional & Skill Gigs</option>
                                        <option value="semi-skilled">Skilled Labor / Semi-Skilled</option>
                                        <option value="informal">Community Gigs (Informal)</option>
                                        <option value="emergency">Instant Tasks (Emergency)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Job Type
                                    </label>
                                    <select
                                        value={type}
                                        onChange={(e) => setType(e.target.value as any)}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] transition-colors appearance-none"
                                    >
                                        <option value="contract">Contract</option>
                                        <option value="full-time">Full-Time</option>
                                        <option value="part-time">Part-Time</option>
                                        <option value="gig">Gig / Task</option>
                                        <option value="one-day">One Day</option>
                                        <option value="hourly">Hourly</option>
                                    </select>
                                </div>
                            </div>

                            {/* Mode & Urgency */}
                            <div className="grid grid-cols-2 gap-4" style={{ pointerEvents: 'auto' }}>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Work Mode
                                    </label>
                                    <select
                                        value={mode}
                                        onChange={(e) => setMode(e.target.value as any)}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] transition-colors appearance-none"
                                    >
                                        <option value="remote">Remote</option>
                                        <option value="on-site">On-Site</option>
                                        <option value="hybrid">Hybrid</option>
                                        <option value="field">Field Work</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Urgency
                                    </label>
                                    <select
                                        value={urgency}
                                        onChange={(e) => setUrgency(e.target.value as any)}
                                        className={`w-full px-4 py-3 border rounded-lg text-white focus:outline-none transition-colors appearance-none ${urgency === 'immediate' ? 'bg-red-900/40 border-red-500/50 text-red-100' : 'bg-black/30 border-[#D4AF37]/30'}`}
                                    >
                                        <option value="flexible">Flexible</option>
                                        <option value="scheduled">Scheduled</option>
                                        <option value="immediate">Immediate / Urgent</option>
                                    </select>
                                </div>
                            </div>

                            {/* Location (if not remote) */}
                            {mode !== 'remote' && (
                                <div className="grid grid-cols-2 gap-4" style={{ pointerEvents: 'auto' }}>
                                    <div>
                                        <label className="block text-[#D4AF37] font-semibold mb-2">City</label>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder="e.g. New York"
                                            className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[#D4AF37] font-semibold mb-2">Country</label>
                                        <input
                                            type="text"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            placeholder="e.g. USA"
                                            className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Budget Range & Pay Structure */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" style={{ pointerEvents: 'auto' }}>
                                <div className="col-span-1">
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Structure
                                    </label>
                                    <select
                                        value={payStructure}
                                        onChange={(e) => setPayStructure(e.target.value as any)}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] appearance-none"
                                    >
                                        <option value="fixed">Fixed Price</option>
                                        <option value="hourly">Hourly Rate</option>
                                        <option value="daily">Daily Rate</option>
                                        <option value="task-based">Per Task</option>
                                    </select>
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Min
                                    </label>
                                    <input
                                        type="number"
                                        value={budgetMin}
                                        onChange={(e) => setBudgetMin(e.target.value)}
                                        min="0"
                                        placeholder="0"
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Max
                                    </label>
                                    <input
                                        type="number"
                                        value={budgetMax}
                                        onChange={(e) => setBudgetMax(e.target.value)}
                                        min="0"
                                        placeholder="∞"
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Curr
                                    </label>
                                    <select
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value)}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] appearance-none"
                                    >
                                        <option value="USD">USD ($)</option>
                                        <option value="ZAR">ZAR (R)</option>
                                        <option value="GBP">GBP (£)</option>
                                        <option value="EUR">EUR (€)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Physical Effort & Languages */}
                            <div className="grid grid-cols-2 gap-4" style={{ pointerEvents: 'auto' }}>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Physical Effort
                                    </label>
                                    <select
                                        value={physicalEffort}
                                        onChange={(e) => setPhysicalEffort(e.target.value as any)}
                                        className="w-full px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] appearance-none"
                                    >
                                        <option value="none">None (Digital/Office)</option>
                                        <option value="light">Light (Walking/Standing)</option>
                                        <option value="moderate">Moderate (Lifting/Manual)</option>
                                        <option value="heavy">Heavy (Labor intensive)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[#D4AF37] font-semibold mb-2">
                                        Languages
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={currentLanguage}
                                            onChange={(e) => setCurrentLanguage(e.target.value)}
                                            placeholder="Add language..."
                                            className="flex-1 px-4 py-3 bg-black/30 border border-[#D4AF37]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] text-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addLanguage();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {selectedLanguages.map((lang, idx) => (
                                            <span key={idx} className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded text-xs flex items-center gap-1 border border-[#D4AF37]/20">
                                                {lang}
                                                <BsX className="cursor-pointer" onClick={() => setSelectedLanguages(selectedLanguages.filter((_, i) => i !== idx))} />
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Booleans: CV & Tools */}
                            <div className="flex flex-wrap gap-6 p-4 bg-black/20 rounded-lg border border-[#D4AF37]/10" style={{ pointerEvents: 'auto' }}>
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div
                                        className={`w-6 h-6 rounded border transition-all flex items-center justify-center ${requiresCV ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#D4AF37]/30 group-hover:border-[#D4AF37]/60'}`}
                                        onClick={() => setRequiresCV(!requiresCV)}
                                    >
                                        {requiresCV && <BsPlusCircle size={14} className="text-[#2C1810] rotate-45" />}
                                    </div>
                                    <span className="text-[#D4AF37] font-medium transition-colors group-hover:text-premium-gold-light">CV Required</span>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div
                                        className={`w-6 h-6 rounded border transition-all flex items-center justify-center ${toolsRequired ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#D4AF37]/30 group-hover:border-[#D4AF37]/60'}`}
                                        onClick={() => setToolsRequired(!toolsRequired)}
                                    >
                                        {toolsRequired && <BsPlusCircle size={14} className="text-[#2C1810]" />}
                                    </div>
                                    <span className="text-[#D4AF37] font-medium transition-colors group-hover:text-premium-gold-light">Tools Required</span>
                                </label>

                                <p className="text-gray-500 text-xs w-full mt-1 italic">
                                    * Uncheck "CV Required" for simple or informal gigs to increase recruitment speed.
                                </p>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
                                    {error}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="order-2 sm:order-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all text-sm sm:text-base"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="order-1 sm:order-2 px-6 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin w-5 h-5 border-2 border-premium-wood-dark border-t-transparent rounded-full"></div>
                                            Dispatching...
                                        </>
                                    ) : (
                                        <>
                                            <BsPlusCircle className="w-5 h-5" />
                                            {initialData ? 'Update Task' : 'Dispatch Task'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default TaskPostingForm;
